import {
  PermissionEntity,
  permissionsSelector,
  RoleEntity,
  useGetAllPermissionsQuery,
  useGetPermissionsByRoleQuery
} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {
  BasePermissionDataTable,
  BasePermissionDataTableProps
} from "../../data-table/PermissionDataTable/BasePermissionDataTable";
import {useCallback, useMemo, useState} from "react";
import {useRoleMutation} from "../../../hooks/useRoleMutation";
import {Switch} from "../../switch/Switch/Switch";
import {PAGINATION_PAGE_SIZES} from "../../data-table/DataTable/usePagination";
import { useAuth } from "../../../providers/AuthProvider/AuthProvider";

export type RoleDetailsProps = {
  role: RoleEntity
}

export const RoleDetails = (props: RoleDetailsProps) => {
  const {role} = props;

  const {t} = useTranslation(["common"]);

  const [activePermissionsOnly, setActivePermissionsOnly] = useState<boolean>(true)

  const selectPermissions = useMemo(() => {
    return permissionsSelector();
  }, []);

  const {allPermissions, isAllPermissionsLoading, allPermissionError,} = useGetAllPermissionsQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      allPermissions: selectPermissions(result),
      isAllPermissionsLoading: result.isLoading,
      allPermissionError: result.error
    })
  });

  const {rolePermissions, isRolePermissionsLoading, rolePermissionError} = useGetPermissionsByRoleQuery(role.id, {
    selectFromResult: (result) => ({
      ...result,
      rolePermissions: selectPermissions(result),
      isRolePermissionsLoading: result.isLoading,
      rolePermissionError: result.error
    })
  });

  const activePermissions = useMemo(() => {
    return new Set<string>(rolePermissions.map(p => p.id))
  }, [rolePermissions])

  const {
    addPermission,
    removePermission,
    isAddingPermission,
    isRemovingPermission,
  } = useRoleMutation()

  const isPermissionsLoading = useMemo(() => {
    return isAllPermissionsLoading || isRolePermissionsLoading || isAddingPermission || isRemovingPermission
  }, [isAddingPermission, isAllPermissionsLoading, isRemovingPermission, isRolePermissionsLoading])

  const togglePermission = useCallback((id: string) => {
    const value = activePermissions.has(id)
    if (value) {
      removePermission(role.id, id, undefined)
    } else {
      addPermission(role.id, id, undefined)
    }
  }, [addPermission, removePermission, role.id, activePermissions])

  return (
    <Grid>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{
              t("common.entity.role.profile.info.section.details.label", "Details")}
            </Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.role.profile.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {role.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.role.profile.info.field.description.label", "Description")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {role.description}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={12}>
        <HasAccess permissions={["permission_view"]}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group position="apart" pb="sm">
              <Title order={4}>{
                t("common.entity.role.profile.info.section.permissions.label", "Permissions")}
              </Title>
            </Group>

            <Switch
              label={t("common.entity.role.profile.action.showactivepermissions", "Show Active Permissions Only")}
              checked={activePermissionsOnly}
              onChange={() => setActivePermissionsOnly(!activePermissionsOnly)}
            />

            <Divider my="md" />

            <RolePermissionDataTable
              selectedPermissions={activePermissions}
              togglePermission={togglePermission}
              verticalSpacing="sm"
              withBorder
              records={activePermissionsOnly ? rolePermissions : allPermissions}
              recordsPerPageOptions={PAGINATION_PAGE_SIZES}
              fetching={isPermissionsLoading}
            />
          </Card>
        </HasAccess>
      </Grid.Col>
    </Grid>
  );
};

export type RolePermissionDataTableProps = Omit<BasePermissionDataTableProps, 'renderActions'> & {
  selectedPermissions: Set<string>
  togglePermission: (id: string) => void
}

export const RolePermissionDataTable = (props: RolePermissionDataTableProps) => {
  const {
    selectedPermissions,
    togglePermission,
    ...rest
  } = props

  const {hasAccess} = useAuth()

  const renderActions = useCallback((record: PermissionEntity, index: number) => {
    return (
      <Switch
        disabled={!hasAccess("role_edit")}
        checked={selectedPermissions.has(record.id)}
        onChange={() => togglePermission(record.id)}
      />
    )
  }, [selectedPermissions, togglePermission, hasAccess])

  return (
    <BasePermissionDataTable
      {...rest}
      renderActions={renderActions}
    />
  )
}
